import React, { useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useTheme } from '../../contexts/ThemeContext';
import LiveChatView from '../../views/crm/LiveChatView';
import '../../views/CRMDashboard.css';
import './FloatingChatWindow.css';

// CRM styling relies on these CSS variables being set on an ancestor (normally
// the .crm-page wrapper). The floating window lives outside that wrapper, so we
// re-declare them here to keep the embedded chat themed correctly.
const crmThemeVars = isDarkMode => ({
    '--crm-surface': isDarkMode ? '#27293d' : '#ffffff',
    '--crm-surface-muted': isDarkMode ? '#202235' : '#f4f6f8',
    '--crm-page': isDarkMode ? '#1e1f2f' : '#eef1f4',
    '--crm-text': isDarkMode ? '#f7f8fa' : '#20242a',
    '--crm-muted': isDarkMode ? '#aeb3c2' : '#66707a',
    '--crm-border': isDarkMode ? '#3a3d55' : '#d9dee4',
    '--crm-accent': '#5e72e4'
});

// Shared counter so the most recently focused window always rises to the top.
let floatingWindowZCounter = 4000;
const PINNED_Z_BASE = 6000;

const FloatingChatWindow = ({ title, channel, defaultPosition, onClose }) => {
    const { isDarkMode } = useTheme();
    const nodeRef = useRef(null);
    const [collapsed, setCollapsed] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [zIndex, setZIndex] = useState(() => (floatingWindowZCounter += 1));

    const bringToFront = () => {
        floatingWindowZCounter += 1;
        setZIndex(floatingWindowZCounter);
    };

    const effectiveZIndex = pinned ? PINNED_Z_BASE + zIndex : zIndex;

    return (
        <Draggable
            handle=".fcw-header"
            cancel=".fcw-btn"
            nodeRef={nodeRef}
            defaultPosition={defaultPosition}
            onStart={bringToFront}
        >
            <div
                ref={nodeRef}
                className={`fcw-window${pinned ? ' fcw-window--pinned' : ''}${collapsed ? ' fcw-window--collapsed' : ''}`}
                style={{ ...crmThemeVars(isDarkMode), zIndex: effectiveZIndex }}
                onMouseDown={bringToFront}
            >
                <div className="fcw-header">
                    <span className="fcw-title">
                        {pinned && <span className="fcw-pin-dot" aria-hidden="true" />}
                        {title}
                    </span>
                    <div className="fcw-header-actions">
                        <button
                            type="button"
                            className={`fcw-btn${pinned ? ' fcw-btn--active' : ''}`}
                            title={pinned ? 'Unpin (allow other windows on top)' : 'Pin on top'}
                            aria-pressed={pinned}
                            onClick={() => {
                                setPinned(prev => !prev);
                                bringToFront();
                            }}
                        >
                            <i className="tim-icons icon-pin" />
                        </button>
                        <button
                            type="button"
                            className="fcw-btn"
                            title={collapsed ? 'Expand' : 'Collapse'}
                            onClick={() => setCollapsed(prev => !prev)}
                        >
                            <i className={`tim-icons ${collapsed ? 'icon-simple-add' : 'icon-simple-delete'}`} />
                        </button>
                        <button type="button" className="fcw-btn fcw-btn--close" title="Close" onClick={onClose}>
                            <i className="tim-icons icon-simple-remove" />
                        </button>
                    </div>
                </div>
                {!collapsed && (
                    <div className="fcw-body">
                        <LiveChatView channel={channel} title={title} />
                    </div>
                )}
            </div>
        </Draggable>
    );
};

export default FloatingChatWindow;
